import { computed, observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { DepartmentResponseArgs, DepartmentArgs } from '../department/department2';
import Department, { convertDepartmentResponseToArgs } from '../department/department2';
import type { GradeResponseArgs } from '../grade2';
import Grade, { convertGradeResponseToArgs } from '../grade2';
import type { OrganizationResponseArgs } from '../organization/organization3';
import Organization, { convertOrganizationResponseToArgs } from '../organization/organization3';
import type { MileageNumberResponseArgs } from '../mileage_number2';
import MileageNumber, { convertMileageNumberResponseToArgs } from '../mileage_number2';
import type { UserMemoResponseArgs } from '../user_memo';
import UserMemo, { convertUserMemoResponseToArgs } from '../user_memo';
import type { UserForwardedEmailResponseArgs } from '../user_forwarded_email2';
import UserForwardedEmail, { convertUserForwardedEmailResponseToArgs } from '../user_forwarded_email2';
import type { NationalityResponseArgs } from '../nationality';
import Nationality, { convertNationalityResponseToArgs } from '../nationality';
import type { OrganizationBaseResponseArgs } from '../organization_base/organization_base2';
import OrganizationBase, { convertOrganizationBaseResponseToArgs } from '../organization_base/organization_base2';
import type { ExicCardResponseArgs } from '../exic_card2';
import ExicCard, { convertExicCardResponseToArgs } from '../exic_card2';

export type UserResponseArgs = {
  id: number;
  name: string;
  department_id?: number;
  name_roman?: string;
  last_name?: string;
  first_name?: string;
  last_name_roman?: string;
  first_name_roman?: string;
  last_name_kana?: string;
  first_name_kana?: string;
  birthday?: string;
  age?: number;
  gender?: string;
  nationalized_tel?: string;
  email?: string;
  tel?: string;
  nationality_id?: number;
  nationalities?: NationalityResponseArgs[]; // TODO: レスポンスでかいから消したい
  department?: DepartmentResponseArgs;
  domestic_approval_required?: boolean;
  foreign_approval_required?: boolean;
  grade?: GradeResponseArgs;
  organization?: OrganizationResponseArgs;
  organization_base?: OrganizationBaseResponseArgs;
  passport_number?: string;
  passport_expire?: string;
  mileage_numbers?: MileageNumberResponseArgs[];
  user_memos?: UserMemoResponseArgs[];
  admin_notification?: number;
  default_seat_classes?: string[];
  freee_auth_uri?: string;
  restriction_distance_and_time_availability?: boolean;
  express_outsourcing_availability?: boolean;
  google_calendar_connected?: boolean;
  outlook_calendar_connected?: boolean;
  auto_sync_trip_to_calendar?: boolean;
  use_slack_app?: boolean;
  ai_travel_slack_token?: string;
  exic_id?: string;
  exic_password?: string;
  exic_card_id?: number;
  exic_cards?: ExicCardResponseArgs[];
  concur_connected?: boolean;
  concur_integration_available?: boolean;
  concur_error?: string | null;
  user_forwarded_emails?: UserForwardedEmailResponseArgs[];
  employee_number?: string;
  show_other_members?: boolean;
};

export type UserArgs = {
  id: number;
  name: string;
  nameRoman?: string;
  lastName?: string;
  firstName?: string;
  lastNameRoman?: string;
  firstNameRoman?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  birthday?: Moment;
  age?: number;
  gender?: string;
  nationalizedTel?: string;
  email?: string;
  tel?: string;
  nationalityId?: number;
  nationalities?: Nationality[]; // TODO: レスポンスでかいから消したい
  department?: DepartmentArgs;
  domesticApprovalRequired?: boolean;
  foreignApprovalRequired?: boolean;
  grade?: Grade;
  organization?: Organization;
  organizationBase?: OrganizationBase;
  passportNumber?: string;
  passportExpire?: string;
  mileageNumbers?: MileageNumber[];
  userMemos?: UserMemo[];
  adminNotification?: number;
  defaultSeatClasses?: string[];
  freeeAuthUri?: string;
  restrictionDistanceAndTimeAvailability?: boolean;
  expressOutsourcingAvailability?: boolean;
  googleCalendarConnected?: boolean;
  outlookCalendarConnected?: boolean;
  autoSyncTripToCalendar?: boolean;
  useSlackApp?: boolean;
  aiTravelSlackToken?: string;
  exicId?: string;
  exicPassword?: string;
  exicCardId?: number;
  exicCards?: ExicCard[];
  concurConnected?: boolean;
  concurIntegrationAvailable?: boolean;
  concurError?: string | null;
  userForwardedEmails?: UserForwardedEmail[];
  employeeNumber?: string;
  showOtherMembers?: boolean;
};

export const convertUserResponseToArgs = (response: UserResponseArgs) => {
  const args: UserArgs = {
    id: response.id,
    name: response.name,
    nameRoman: response.name_roman,
    lastName: response.last_name,
    firstName: response.first_name,
    lastNameRoman: response.last_name_roman,
    firstNameRoman: response.first_name_roman,
    lastNameKana: response.last_name_kana,
    firstNameKana: response.first_name_kana,
    birthday: response.birthday ? moment(response.birthday) : undefined,
    age: response.age,
    gender: response.gender,
    nationalizedTel: response.nationalized_tel,
    email: response.email,
    tel: response.tel,
    nationalityId: response.nationality_id,
    nationalities:
      response.nationalities?.map(nationality => new Nationality(convertNationalityResponseToArgs(nationality))) ||
      [],
    department: response.department ? convertDepartmentResponseToArgs(response.department) : undefined,
    domesticApprovalRequired: response.domestic_approval_required,
    foreignApprovalRequired: response.foreign_approval_required,
    grade: response.grade ? new Grade(convertGradeResponseToArgs(response.grade)) : undefined,
    organization: response.organization
      ? new Organization(convertOrganizationResponseToArgs(response.organization))
      : undefined,
    organizationBase: response.organization_base
      ? new OrganizationBase(convertOrganizationBaseResponseToArgs(response.organization_base))
      : undefined,
    passportNumber: response.passport_number,
    passportExpire: response.passport_expire,
    mileageNumbers:
      response.mileage_numbers?.map(
        mileageNumber => new MileageNumber(convertMileageNumberResponseToArgs(mileageNumber))
      ) || [],
    userMemos: response.user_memos?.map(userMemo => new UserMemo(convertUserMemoResponseToArgs(userMemo))) || [],
    adminNotification: response.admin_notification,
    defaultSeatClasses: response.default_seat_classes,
    freeeAuthUri: response.freee_auth_uri,
    restrictionDistanceAndTimeAvailability: response.restriction_distance_and_time_availability,
    expressOutsourcingAvailability: response.express_outsourcing_availability,
    googleCalendarConnected: response.google_calendar_connected,
    outlookCalendarConnected: response.outlook_calendar_connected,
    autoSyncTripToCalendar: response.auto_sync_trip_to_calendar,
    useSlackApp: response.use_slack_app,
    aiTravelSlackToken: response.ai_travel_slack_token,
    exicId: response.exic_id,
    exicPassword: response.exic_password,
    exicCardId: response.exic_card_id,
    exicCards: response.exic_cards?.map(exicCard => new ExicCard(convertExicCardResponseToArgs(exicCard))) || [],
    concurConnected: response.concur_connected,
    concurIntegrationAvailable: response.concur_integration_available,
    concurError: response.concur_error,

    userForwardedEmails:
      response.user_forwarded_emails?.map(
        userForwardedEmail => new UserForwardedEmail(convertUserForwardedEmailResponseToArgs(userForwardedEmail))
      ) || [],
    employeeNumber: response.employee_number,
    showOtherMembers: response.show_other_members
  };
  return args;
};

export default class User {
  @observable id: number;
  @observable name: string;
  @observable nameRoman?: string;
  @observable lastName?: string;
  @observable firstName?: string;
  @observable lastNameRoman?: string;
  @observable firstNameRoman?: string;
  @observable lastNameKana?: string;
  @observable firstNameKana?: string;
  @observable birthday?: Moment;
  @observable age?: number;
  @observable gender?: string;
  @observable nationalizedTel?: string;
  @observable email?: string;
  @observable tel?: string;
  @observable nationalityId?: number;
  @observable nationalities?: Nationality[]; // TODO: レスポンスでかいから消したい
  @observable department?: Department;
  @observable domesticApprovalRequired?: boolean;
  @observable foreignApprovalRequired?: boolean;
  @observable grade?: Grade;
  @observable organization?: Organization;
  @observable organizationBase?: OrganizationBase;
  @observable passportNumber?: string;
  @observable passportExpire?: string;
  @observable mileageNumbers?: MileageNumber[];
  @observable userMemos?: UserMemo[];
  @observable adminNotification?: number;
  @observable defaultSeatClasses?: string[];
  @observable freeeAuthUri?: string;
  @observable restrictionDistanceAndTimeAvailability?: boolean;
  @observable expressOutsourcingAvailability?: boolean;
  @observable googleCalendarConnected?: boolean;
  @observable outlookCalendarConnected?: boolean;
  @observable autoSyncTripToCalendar?: boolean;
  @observable useSlackApp?: boolean;
  @observable aiTravelSlackToken?: string;
  @observable exicId?: string;
  @observable exicPassword?: string;
  @observable exicCardId?: number;
  @observable exicCards?: ExicCard[];
  @observable concurConnected?: boolean;
  @observable concurIntegrationAvailable?: boolean;
  @observable concurError?: string | null;
  @observable userForwardedEmails?: UserForwardedEmail[];
  @observable employeeNumber?: string;
  @observable showOtherMembers?: boolean;

  constructor(args: UserArgs) {
    this.id = args.id;
    this.name = args.name;
    this.nameRoman = args.nameRoman;
    this.lastName = args.lastName;
    this.firstName = args.firstName;
    this.lastNameRoman = args.lastNameRoman;
    this.firstNameRoman = args.firstNameRoman;
    this.lastNameKana = args.lastNameKana;
    this.firstNameKana = args.firstNameKana;
    this.birthday = args.birthday;
    this.age = args.age;
    this.gender = args.gender;
    this.nationalizedTel = args.nationalizedTel;
    this.email = args.email;
    this.tel = args.tel;
    this.nationalityId = args.nationalityId;
    this.nationalities = args.nationalities;
    this.department = args.department ? new Department(args.department) : undefined;
    this.domesticApprovalRequired = args.domesticApprovalRequired;
    this.foreignApprovalRequired = args.foreignApprovalRequired;
    this.grade = args.grade;
    this.organization = args.organization;
    this.organizationBase = args.organizationBase;
    this.passportNumber = args.passportNumber;
    this.passportExpire = args.passportExpire;
    this.mileageNumbers = args.mileageNumbers;
    this.userMemos = args.userMemos;
    this.adminNotification = args.adminNotification;
    this.defaultSeatClasses = args.defaultSeatClasses;
    this.freeeAuthUri = args.freeeAuthUri;
    this.restrictionDistanceAndTimeAvailability = args.restrictionDistanceAndTimeAvailability;
    this.expressOutsourcingAvailability = args.expressOutsourcingAvailability;
    this.googleCalendarConnected = args.googleCalendarConnected;
    this.outlookCalendarConnected = args.outlookCalendarConnected;
    this.autoSyncTripToCalendar = args.autoSyncTripToCalendar;
    this.useSlackApp = args.useSlackApp;
    this.aiTravelSlackToken = args.aiTravelSlackToken;
    this.exicId = args.exicId;
    this.exicPassword = args.exicPassword;
    this.exicCardId = args.exicCardId;
    this.exicCards = args.exicCards;
    this.concurConnected = args.concurConnected;
    this.concurIntegrationAvailable = args.concurIntegrationAvailable;
    this.concurError = args.concurError;
    this.userForwardedEmails = args.userForwardedEmails;
    this.employeeNumber = args.employeeNumber;
    this.showOtherMembers = args.showOtherMembers;
  }

  removeMileageNumber(index: number) {
    this.mileageNumbers = this.mileageNumbers?.filter((_m, i) => i !== index) || [];
  }

  addMileageNumber() {
    this.mileageNumbers = [...(this.mileageNumbers || []), { carrier: 'ANA', number: '' }];
  }

  addUserForwardedEmail() {
    this.userForwardedEmails = [...(this.userForwardedEmails || []), new UserForwardedEmail({ email: '' })];
  }

  @computed get emailAndName(): string {
    return `${this.name} (${this.email})`;
  }
}
